import { capitalize } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Row from 'components/Row';
import Text from 'components/Text';
import TextBadge from 'components/TextBadge';
import TextWithIcon from 'components/TextWithIcon';
import {
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_participantMetadata,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_coachingSessions,
} from 'coaching/queries/__generated__/GetCoachingHomeDashboard';
import CoachingSessionInfoContainerBody from 'coaching/CoachingSessionInfoContainerBody';
import MediaObject from 'components/MediaObject';
import CoachingConstants from 'coaching/CoachingConstants';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';

import 'components/styles/CoachingSessionInfoContainer.scss';

export type ParticipantMetadata = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_participantMetadata;
export type CoachingSession = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_coachingSessions;

type Props = {
  userUuid?: string;
  participantMetadata: ParticipantMetadata;
  coachingSession?: CoachingSession;
};

export enum CoachingSessionState {
  FREE_TRIAL_WITH_UPCOMING_SESSION = 0,
  HAS_NO_NEXT_SESSION = 1,
  HAS_NEXT_SESSION = 2,
  UNKNOWN = 3,
}

export default class CoachingSessionInfoContainer extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  /**
   * Format like "Weekly . Thu at 4:00 PM"
   */
  constructCadanceString() {
    if (
      !this.props.coachingSession?.frequency ||
      !this.props.coachingSession?.nextInstanceStartDateTime
    ) {
      return undefined;
    }
    const cadence = capitalize(this.props.coachingSession?.frequency);
    const nextSession = moment(this.props.coachingSession?.nextInstanceStartDateTime);
    const dayOfWeekAbbreviation = nextSession.format('ddd');
    const time = nextSession.format('LT');
    return cadence + ' • ' + dayOfWeekAbbreviation + ' at ' + time;
  }

  calculateCoachingSessionState() {
    if (!this.props.coachingSession?.nextInstanceStartDateTime) {
      return CoachingSessionState.HAS_NO_NEXT_SESSION;
    } else if (this.props.coachingSession?.isFreeTrial) {
      return CoachingSessionState.FREE_TRIAL_WITH_UPCOMING_SESSION;
    } else if (this.props.coachingSession?.nextInstanceStartDateTime) {
      return CoachingSessionState.HAS_NEXT_SESSION;
    } else {
      return CoachingSessionState.UNKNOWN;
    }
  }

  constructMessageCoachButton() {
    if (!this.props.coachingSession?.streamUuidForSession) {
      return undefined;
    }

    const toUrl = userAgent.isDesktop()
      ? `/coaching/messages/${this.props.coachingSession?.streamUuidForSession}`
      : CoachingConstants.COACHING_GET_APP_ROUTE;

    return (
      <Button to={toUrl} level="secondary" size="xxsmall">
        Message coach
      </Button>
    );
  }

  /**
   * Free trial is reschedule
   * No more sessions is blank
   * ongoing is "edit schedule"
   */
  calculateScheduleButtonText() {
    const sessionState = this.calculateCoachingSessionState();
    switch (sessionState) {
      case CoachingSessionState.HAS_NO_NEXT_SESSION:
        return undefined;
      case CoachingSessionState.FREE_TRIAL_WITH_UPCOMING_SESSION:
        return 'Request reschedule';
      case CoachingSessionState.HAS_NEXT_SESSION:
        return 'Request reschedule';
    }
  }

  constructSessionHeader() {
    const scheduleButtonText = this.calculateScheduleButtonText();
    return (
      <MediaObject figure="right" gutter="small" center>
        <MediaObject center gutter="small">
          <Text weight="semibold" size="medium">
            {/* We only allow coaching on one subject as of now */}
            {this.props.participantMetadata.subjects[0]}
          </Text>
          {this.props.coachingSession?.isFreeTrial ? (
            <TextBadge color="wolf" textSize="xxxxsmall">
              Free trial
            </TextBadge>
          ) : (
            <Text size="xsmall" color="thunder">
              {!this.props.coachingSession?.isFreeTrial && this.constructCadanceString()}
            </Text>
          )}
        </MediaObject>
        {scheduleButtonText && (
          <Button level="icon">
            <TextWithIcon
              className="CoachingSessionInfoContainer-rescheduleButton"
              icon="pencil-compose-stroke"
              size="small"
              weight="semibold">
              {scheduleButtonText}
            </TextWithIcon>
          </Button>
        )}
      </MediaObject>
    );
  }

  constructJoinSessionButton() {
    const minutesFromNow = moment(this.props.coachingSession?.nextInstanceStartDateTime).diff(
      moment.now(),
      'minutes'
    );
    const isDisabled = minutesFromNow > 30;

    const toUrl = userAgent.isDesktop()
      ? `/coaching/session/${this.props.participantMetadata.distanceLearningPermalink}`
      : CoachingConstants.COACHING_GET_APP_ROUTE;

    return (
      <Button disabled={isDisabled} to={toUrl} level="primary" size="xxsmall">
        Join session
      </Button>
    );
  }

  render() {
    return (
      <Row>
        <Box noShadow textAlign="left" bandHeader={this.constructSessionHeader()}>
          <BoxBody full size="small" divided={true}>
            <CoachingSessionInfoContainerBody
              joinSessionButton={this.constructJoinSessionButton()}
              messageCoachButton={this.constructMessageCoachButton()}
              userUuid={this.props.userUuid}
              participantMetadata={this.props.participantMetadata}
              coachingSession={this.props.coachingSession}
              coachingSessionState={this.calculateCoachingSessionState()}
            />
          </BoxBody>
        </Box>
      </Row>
    );
  }
}
